import { useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { DoorOpen, DoorClosed, Copy, Check } from "lucide-react";
import CoursesTab from "./CoursesTab";
import QuestionsTab from "./QuestionsTab";
import CandidatesTab from "./CandidatesTab";
import RankingTab from "./RankingTab";
import SettingsTab from "./SettingsTab";
import AccountTab from "./AccountTab";

export default function AdminDashboard() {
  const [tab, setTab] = useState("candidates");
  const [settings, setSettings] = useState(null);

  const loadSettings = () => api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
  useEffect(() => { loadSettings(); }, []);

  const toggleOpen = async (v) => {
    try {
      await api.put("/admin/settings", { applications_open: v });
      setSettings((s) => ({ ...s, applications_open: v }));
      toast.success(v ? "Candidature aperte" : "Candidature chiuse");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [copied, setCopied] = useState(false);
  const applyUrl = settings?.slug ? `${window.location.origin}/candidati/${settings.slug}` : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(applyUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = applyUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="eyebrow mb-2" data-testid="admin-tenant-name">{settings?.tenant_name || "Amministrazione istituto"}</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Pannello di controllo</h1>
          </div>
          {settings && (
            <div data-testid="applications-open-flag"
              className={`w-full sm:w-auto rounded-xl border-2 px-5 py-4 ${settings.applications_open ? "border-green-500 bg-green-50" : "border-slate-300 bg-slate-50"}`}>
              <div className="flex items-center gap-4">
                {settings.applications_open
                  ? <DoorOpen className="h-6 w-6 shrink-0 text-green-600" />
                  : <DoorClosed className="h-6 w-6 shrink-0 text-slate-500" />}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    Candidature {settings.applications_open ? "aperte" : "chiuse"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {settings.applications_open
                      ? "La pagina pubblica accetta candidature"
                      : "Nota: le candidature sono chiuse — il link mostra «Selezione non ancora aperta»"}
                  </div>
                </div>
                <Switch data-testid="applications-open-toggle" checked={Boolean(settings.applications_open)} onCheckedChange={toggleOpen} />
              </div>
              <div className={`mt-3 border-t pt-3 ${settings.applications_open ? "border-green-200" : "border-slate-200"}`}>
                <div className="mb-1 text-xs font-semibold text-slate-700">Link per la candidatura</div>
                <div className="flex items-start gap-2">
                  <span data-testid="application-link-text" className="min-w-0 flex-1 break-all font-mono2 text-xs text-slate-600">{applyUrl}</span>
                  <button type="button" data-testid="application-link-copy-button" onClick={copyLink}
                    className="shrink-0 rounded-md p-1.5 text-slate-500 transition-colors hover:bg-white/70 hover:text-slate-900"
                    aria-label="Copia il link per la candidatura" title="Copia il link">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                {copied && <div data-testid="link-copied-feedback" className="mt-1 text-xs font-semibold text-green-600">Link copiato</div>}
              </div>
            </div>
          )}
        </div>
        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger data-testid="admin-nav-candidates" value="candidates">Candidature</TabsTrigger>
            <TabsTrigger data-testid="admin-nav-ranking" value="ranking">Graduatoria</TabsTrigger>
            <TabsTrigger data-testid="admin-nav-courses" value="courses">Corsi</TabsTrigger>
            <TabsTrigger data-testid="admin-nav-questions" value="questions">Banca domande</TabsTrigger>
            <TabsTrigger data-testid="admin-nav-settings" value="settings">Impostazioni</TabsTrigger>
            <TabsTrigger data-testid="admin-nav-account" value="account">Account</TabsTrigger>
          </TabsList>
          <TabsContent value="candidates"><CandidatesTab /></TabsContent>
          <TabsContent value="ranking"><RankingTab /></TabsContent>
          <TabsContent value="courses"><CoursesTab /></TabsContent>
          <TabsContent value="questions"><QuestionsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
          <TabsContent value="account"><AccountTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
